import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch } from 'src/Hooks/redux'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import {
  OrderItem,
  OrderItemType,
  useAddOrderItemMutation,
  useRemoveOrderItemsMutation,
  useUpdateOrderItemMutation,
} from 'src/Redux/artistOrderApi'

export const PRIORITIZED_LAUNCH_RATE = 0.05
export const PRIORITIZED_LAUNCH_MAX_PRICE = 10
const PRIORITIZED_LAUNCH_PRICE_DECIMALS = 2

export const getPrioritizedLaunchPrice = (budget: number): number =>
  Number(
    Math.min(
      budget * PRIORITIZED_LAUNCH_RATE,
      PRIORITIZED_LAUNCH_MAX_PRICE,
    ).toFixed(PRIORITIZED_LAUNCH_PRICE_DECIMALS),
  )

export type PrioritizedLaunchAddOnType =
  | OrderItemType.SpotifyPrioritizedLaunch
  | OrderItemType.TiktokPrioritizedLaunch

export interface UsePrioritizedLaunchAddOnParams {
  orderId?: number
  orderItems?: OrderItem[]
  campaignId: number
  budget: number
  addOnType: PrioritizedLaunchAddOnType
  isEligible: boolean
  isOrderMutable: boolean
  onAfterMutation: () => Promise<void>
}

export interface UsePrioritizedLaunchAddOnResult {
  isEnabled: boolean
  price: number
  onChange: (next: boolean) => void
}

export const usePrioritizedLaunchAddOn = ({
  orderId,
  orderItems,
  campaignId,
  budget,
  addOnType,
  isEligible,
  isOrderMutable,
  onAfterMutation,
}: UsePrioritizedLaunchAddOnParams): UsePrioritizedLaunchAddOnResult => {
  const dispatch = useAppDispatch()
  const [addOrderItem] = useAddOrderItemMutation()
  const [updateOrderItem] = useUpdateOrderItemMutation()
  const [removeOrderItems] = useRemoveOrderItemsMutation()
  const isMutationPendingRef = useRef(false)
  const requestedPriceRef = useRef<number | null>(null)
  const [mutationGeneration, setMutationGeneration] = useState(0)

  const price = getPrioritizedLaunchPrice(budget)

  const existingItem = orderItems?.find(
    (item) => item.type === addOnType && item.sourceId === campaignId,
  )
  const isEnabled = Boolean(existingItem)

  const settleMutation = useCallback((): void => {
    isMutationPendingRef.current = false
    setMutationGeneration((generation) => generation + 1)
  }, [])

  const isPriceOutdated = isEnabled && existingItem?.fullPrice !== price

  useEffect(() => {
    if (!isEligible || !isOrderMutable || !isPriceOutdated) {
      return
    }

    if (!orderId || !existingItem || isMutationPendingRef.current) {
      return
    }

    if (requestedPriceRef.current === price) {
      return
    }

    isMutationPendingRef.current = true
    requestedPriceRef.current = price

    updateOrderItem({
      orderId,
      itemId: existingItem.id,
      fullPrice: price,
    })
      .unwrap()
      .then(onAfterMutation)
      .catch(() => {
        dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
      })
      .finally(settleMutation)
  }, [
    dispatch,
    existingItem,
    isEligible,
    isOrderMutable,
    isPriceOutdated,
    mutationGeneration,
    onAfterMutation,
    orderId,
    price,
    settleMutation,
    updateOrderItem,
  ])

  const onChange = (next: boolean): void => {
    if (!orderId || !isOrderMutable || isMutationPendingRef.current) {
      return
    }

    if (next === isEnabled) {
      return
    }

    isMutationPendingRef.current = true
    requestedPriceRef.current = next ? price : null

    const mutation = next
      ? addOrderItem({
          orderId,
          type: addOnType,
          sourceId: campaignId,
          fullPrice: price,
        })
      : removeOrderItems({
          orderId,
          itemIds: existingItem ? [existingItem.id] : [],
        })

    mutation
      .unwrap()
      .then(onAfterMutation)
      .catch(() => {
        dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
      })
      .finally(settleMutation)
  }

  return { isEnabled, price, onChange }
}
